/**
 * Test User Configuration
 * 
 * This file contains dedicated test users for automated testing.
 * These credentials are separate from production users and should NOT be changed
 * unless you're intentionally updating the test suite.
 * 
 * These users are ONLY for testing - they don't affect production users.
 */

import type { UserRole } from '../../src/data/users'

// Helper function for test password hashing (matches production)
const hashPasswordSync = (password: string): string => {
  let hash = 0
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16)
}

export interface TestUser {
  id: number
  username: string
  email: string
  name: string
  role: UserRole
  password: string // Plain text for tests
  password_hash: string
  created_at: string
  active: boolean
}

// Stable test users - DO NOT CHANGE these credentials without updating all tests
export const TEST_USERS: Record<UserRole, TestUser> = {
  admin: {
    id: 101,
    username: 'test-admin',
    email: 'test-admin@example.com',
    name: 'Test Administrator',
    role: 'admin',
    password: 'admin123', // Stable test password
    password_hash: hashPasswordSync('admin123'),
    created_at: '2024-01-01T00:00:00Z',
    active: true
  },
  user: {
    id: 102,
    username: 'test-user',
    email: 'test-user@example.com',
    name: 'Test User',
    role: 'user',
    password: 'user123', // Stable test password
    password_hash: hashPasswordSync('user123'),
    created_at: '2024-01-01T00:00:00Z',
    active: true
  },
  manager: {
    id: 103,
    username: 'test-manager',
    email: 'test-manager@example.com',
    name: 'Test Manager',
    role: 'manager',
    password: 'manager123', // Stable test password
    password_hash: hashPasswordSync('manager123'),
    created_at: '2024-01-01T00:00:00Z',
    active: true
  },
  employee: {
    id: 104,
    username: 'test-employee',
    email: 'test-employee@example.com',
    name: 'Test Employee',
    role: 'employee',
    password: 'employee123', // Stable test password
    password_hash: hashPasswordSync('employee123'),
    created_at: '2024-01-01T00:00:00Z',
    active: true
  }
} as const

// Additional dedicated users for specific functionality
export const ADDITIONAL_TEST_USERS: TestUser[] = [
  {
    id: 105,
    username: 'wade',
    email: 'zeiglerofficemail@gmail.com',
    name: 'Wade Eggleston',
    role: 'employee',
    password: 'timesheet2024', // Wade's expected password
    password_hash: hashPasswordSync('timesheet2024'),
    created_at: '2024-01-01T00:00:00Z',
    active: true
  }
]

// Helper functions for tests
export const getTestUserByRole = (role: UserRole): TestUser => {
  return TEST_USERS[role]
}

export const getTestUserCredentials = (role: UserRole) => {
  const user = TEST_USERS[role]
  return {
    username: user.username,
    password: user.password,
    expectedRole: user.role
  }
}

// Get all test users as an array (for mock API)
export const getAllTestUsers = (): TestUser[] => {
  return [...Object.values(TEST_USERS), ...ADDITIONAL_TEST_USERS]
}

// Default admin user for most tests
export const getDefaultTestAdmin = () => getTestUserCredentials('admin')

// Validate test password (for mock API)
export const validateTestPassword = (inputPassword: string, storedHash: string): boolean => {
  return hashPasswordSync(inputPassword) === storedHash
} 